import React, { useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { stopEvent } from '@aytm/lib/common/func'

import '@aytm/lib/rfd-ui/styles/basic/check-box.scss'

CheckBox.propTypes = {
  /**
   * is checked
   */
  checked: PropTypes.bool,
  /**
   * is disabled
   */
  disabled: PropTypes.bool,
  /**
   * error state
   */
  error: PropTypes.bool,
  /**
   * Checkbox label
   */
  label: PropTypes.string,
  /**
   * check size in pixels
   */
  checkSize: PropTypes.number,
  /**
   * label size in pixels
   */
  labelSize: PropTypes.number,
  /**
   * lineHeight for check and label
   */
  lineHeight: PropTypes.number,
  /**
   * custom style
   */
  style: PropTypes.object,
  /**
   * custom className
   */
  className: PropTypes.string,
  /**
   * "checked" icon className
   */
  checkedIcon: PropTypes.string,
  /**
   * "unchecked" icon className
   */
  uncheckedIcon: PropTypes.string,
  /**
   * fires when CheckBox is `clicked` and `not disabled`: `onAction(event)`
   */
  onAction: PropTypes.func,
  /**
   * fires when CheckBox is `clicked` and `not disabled`: `onChange(!checked)`
   */
  onChange: PropTypes.func,
  /**
   * fires when CheckBox is `clicked` (disabled or not): `onClick(event)`
   */
  onClick: PropTypes.func,
  /**
   * onKeyDown callback
   */
  onKeyDown: PropTypes.func,
  /**
   * `SPACE` and `ENTER` keys presses works as clicks
   */
  spaceEnterAsClick: PropTypes.bool,
  /**
   * `UP` and `DOWN` key presses will move focus between CheckBoxes
   */
  upDownAsMoveFocus: PropTypes.bool,
  /**
   * CheckBox group name for `UP` and `DOWN` keys presses navigation
   */
  groupName: PropTypes.string
}
CheckBox.defaultProps = {
  checkedIcon: 'icon-aytmfd-afdcheckbox-full',
  uncheckedIcon: 'icon-aytmfd-afdcheckbox',
  spaceEnterAsClick: true,
  upDownAsMoveFocus: false,
  groupName: 'default'
}
export default function CheckBox({
  checked,
  disabled,
  error,
  label,
  checkSize,
  labelSize,
  lineHeight,
  style,
  className,
  checkedIcon = 'icon-aytmfd-afdcheckbox-full',
  uncheckedIcon = 'icon-aytmfd-afdcheckbox',
  onAction,
  onChange,
  onClick: onCustomClick,
  onKeyDown: onCustomKeyDown,
  spaceEnterAsClick = true,
  upDownAsMoveFocus = false,
  groupName = 'default',
  ...otherProps
} = {}) {
  const commonLineHeight = useMemo(
    () =>
      lineHeight
        ? `${lineHeight}px`
        : (() => {
            const maxSize = Math.max(checkSize, labelSize)
            return maxSize ? `${maxSize * 1.2}px` : null
          })(),
    [lineHeight, checkSize, labelSize]
  )
  const applyStyle = useMemo(() => (commonLineHeight ? { lineHeight: commonLineHeight } : {}), [commonLineHeight])
  const mergedStyle = useMemo(
    () => (null != style ? Object.assign(applyStyle, style) : applyStyle),
    [style, applyStyle]
  )

  const onClick = useCallback(
    event => {
      if (onCustomClick) {
        onCustomClick(event)
      }
      if (!disabled && (void 0 === event.button || 0 === event.button)) {
        if (onAction) {
          onAction(event)
        }
        if (onChange) {
          onChange(!checked)
        }
      }
    },
    [onCustomClick, onAction, onChange, disabled, checked]
  )

  const onKeyDown = useCallback(
    event => {
      if (onCustomKeyDown) {
        onCustomKeyDown(event)
      }
      if (!disabled) {
        if (spaceEnterAsClick && onClick && (' ' === event.key || 'Enter' === event.key)) {
          onClick(event)
          stopEvent(event)
        } else if (upDownAsMoveFocus) {
          if ('ArrowUp' === event.key) {
            const activeCheckboxes = document.querySelectorAll(
              `${groupName ? `.rfd-ui-checkbox-group-${groupName}` : ''}.rfd-ui-checkbox[tabindex="0"]`
            )
            for (let i = 0; i < activeCheckboxes.length; i++) {
              if (document.activeElement === activeCheckboxes[i]) {
                if (i > 0) {
                  activeCheckboxes[i - 1].focus()
                }
                break
              }
            }
            stopEvent(event)
          } else if ('ArrowDown' === event.key) {
            const activeCheckboxes = document.querySelectorAll(
              `${groupName ? `.rfd-ui-checkbox-group-${groupName}` : ''}.rfd-ui-checkbox[tabindex="0"]`
            )
            for (let i = 0; i < activeCheckboxes.length; i++) {
              if (document.activeElement === activeCheckboxes[i]) {
                if (i < activeCheckboxes.length - 1) {
                  activeCheckboxes[i + 1].focus()
                }
                break
              }
            }
            stopEvent(event)
          }
        }
      }
    },
    [onCustomKeyDown, onClick, disabled, spaceEnterAsClick, upDownAsMoveFocus, groupName]
  )

  return (
    <span
      className={`rfd-ui-checkbox${className ? ' ' + className : ''}${checked ? ' rfd-ui-checkbox-checked' : ''}${
        disabled ? ' rfd-ui-checkbox-disabled' : ''
      }${error ? ' rfd-ui-checkbox-error' : ''}${groupName ? ` rfd-ui-checkbox-group-${groupName}` : ''}`}
      tabIndex={disabled ? -1 : 0}
      style={mergedStyle}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role="checkbox"
      aria-checked={checked}
      aria-disabled={disabled ? true : null}
      {...otherProps}
    >
      <i
        className={`rfd-ui-checkbox-icon ${checked ? checkedIcon : uncheckedIcon}`}
        style={{ fontSize: checkSize, lineHeight: commonLineHeight }}
        role="presentation"
      />
      {label && (
        <span
          className="rfd-ui-checkbox-label"
          dangerouslySetInnerHTML={{ __html: label }}
          style={{ fontSize: labelSize, lineHeight: commonLineHeight }}
        />
      )}
    </span>
  )
}
